import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

/**
 * Season picker for the plain ERB create page.
 *
 * There are 45 seasons and the native select opened taller than the screen, with
 * `test` in it and `spring` sitting next to `spring2026` with nothing to say which
 * you want. This is react-select for the typeahead, grouped and ranked by
 * Shop::SeasonChoices -- the same data the wizard's own picker reads, so the two
 * cannot drift.
 *
 * It writes to a HIDDEN INPUT rather than owning the submit: the create form stays
 * a plain submit-and-redirect, which is what CLAUDE.md keeps in HTML. Nothing here
 * fetches or navigates.
 *
 * Free text is deliberately impossible. A season is matched against
 * `student_images.folder` exactly, so a typo makes a product no student can ever
 * see and nothing surfaces the mistake -- which is why this is a picker and not an
 * input with suggestions.
 */
const SeasonSelect = ({
  name, value, choices, placeholder, required,
}) => {
  const options = Object.entries(
    choices.reduce((groups, choice) => {
      const key = choice.group;
      return { ...groups, [key]: [...(groups[key] || []), choice] };
    }, {}),
  ).map(([label, list]) => ({
    label,
    options: list.map((c) => ({ value: c.value, label: c.label })),
  }));

  const flat = options.flatMap((g) => g.options);
  const [selected, setSelected] = useState(flat.find((o) => o.value === value) || null);

  return (
    <>
      <input type="hidden" name={name} value={selected ? selected.value : ''} />
      <Select
        options={options}
        value={selected}
        onChange={setSelected}
        placeholder={placeholder}
        isClearable={!required}
        // The page is Bootstrap 3; keep the control the same height as its
        // neighbours and let the menu escape the form's overflow.
        styles={{
          control: (base) => ({ ...base, minHeight: 34 }),
          menu: (base) => ({ ...base, zIndex: 5 }),
          groupHeading: (base) => ({
            ...base, fontSize: 11, textTransform: 'none', color: '#666',
          }),
        }}
      />
    </>
  );
};

SeasonSelect.propTypes = {
  // The form field the hidden input posts as, e.g. "shop_product[season]".
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  // Shop::SeasonChoices#as_json
  choices: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    group: PropTypes.string,
  })),
  placeholder: PropTypes.string,
  required: PropTypes.bool,
};

SeasonSelect.defaultProps = {
  value: null,
  choices: [],
  placeholder: 'Search seasons…',
  required: false,
};

export default SeasonSelect;
